import {
  type FC, useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { CONFIG } from '~/_shared/constants/config';
import { CLASSIC_EDIT_BOUNDARY_COUNT_THRESHOLD } from '~/boundary/editMode/boundaryEditModal/constants';
import {
  type ModalProps, ModalType,
} from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { getBoundaryStyle } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryItem.helpers';
import { useBoundaryItems } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryItems';
import { useBoundarySelectEditCustomBoundary } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/useBoundarySelectEditCustomBoundary';
import {
  checkBoundaryIsCustom, useCustomBoundaryGroupsSelector, useGetBoundaryGroup, useGlobalBoundaryGroups,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useGetBoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { boundaryDrawAdvancedEditOpenModal } from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.actionCreators';
import type { BoundarySelectBucket } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import { hexColorToBucketColorInfo } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.utils';
import { mapSettingsBoundariesClearIsolatedBoundaries } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import {
  boundarySelectEditCloseModal, boundarySelectEditCustomBoundaryOpenModal,
  boundarySelectQuickEditCustomBoundaryOpenModal, boundarySelectQuickEditSubmitted,
} from '../../../store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import { clearBoundaryLocationsFilter } from '../../../store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import {
  BoundaryEditModalComponent, type BoundaryEditResults,
} from './boundaryEditModal.component';
import {
  BoundaryEditAction, BoundaryEditManualMethod, BoundaryEditSelectFrom,
} from './boundaryEditModal.helpers';

export type BoundaryEditModalContainerProps = ModalProps<{
  boundaryTerritoryGroupId: number;
  boundaryId?: number;
}>;

export const BoundaryEditModalContainer: FC<BoundaryEditModalContainerProps> = (props) => {
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();
  const boundaryTerritoryGroup = getBoundaryTerritoryGroupById(props.boundaryTerritoryGroupId);
  const dispatch = useDispatch();
  const { openModal: showLoader, closeModal: hideLoader } = useModal(ModalType.Loader);
  const globalBoundaryGroups = useGlobalBoundaryGroups();
  const customBoundaryGroups = useCustomBoundaryGroupsSelector();
  const {
    sortedBoundaryList,
  } = useBoundaryItems({ boundaryTerritoryGroup });
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const { onBoundarySelectEditCustomBoundarySubmit } = useBoundarySelectEditCustomBoundary();

  const isAdvancedEdit = props.boundaryId !== undefined;

  const editedBoundaryGroup = useMemo(() => getBoundaryGroupById(boundaryTerritoryGroup?.boundaryGroupId),
    [boundaryTerritoryGroup, getBoundaryGroupById]);

  const defaultBoundaryGroup = useMemo(() => {
    if (!boundaryTerritoryGroup) {
      return undefined;
    }

    const preferredBG = boundaryTerritoryGroup.settings.selectionEditPreferredBoundaryGroup?.id;
    const originBG = editedBoundaryGroup?.createdFrom?.boundaryGroupIds?.[0];

    const defaultBgId = preferredBG ?? originBG;
    const defaultBg = getBoundaryGroupById(defaultBgId);

    return defaultBg?.visible ? defaultBg : undefined;
  }, [boundaryTerritoryGroup, editedBoundaryGroup?.createdFrom?.boundaryGroupIds, getBoundaryGroupById]);

  const isDefaultBoundaryGroupCustom = checkBoundaryIsCustom(defaultBoundaryGroup);

  const defaultManualMethod = useMemo(() => {
    if (defaultBoundaryGroup !== undefined) {
      return BoundaryEditManualMethod.Selection;
    }

    if (isAdvancedEdit) {
      return BoundaryEditManualMethod.Draw;
    }

    return undefined;
  }, [defaultBoundaryGroup, isAdvancedEdit]);

  const defaultSelectFrom = useMemo(() => {
    if (!defaultBoundaryGroup) {
      return undefined;
    }

    return isDefaultBoundaryGroupCustom ? BoundaryEditSelectFrom.Territory : BoundaryEditSelectFrom.Boundary;
  }, [defaultBoundaryGroup, isDefaultBoundaryGroupCustom]);

  const defaultBoundaryGroupParams = useMemo(() => {
    if (!defaultBoundaryGroup) {
      return undefined;
    }

    return isDefaultBoundaryGroupCustom ? ({
      custom: defaultBoundaryGroup?.id,
    }) : ({
      global: defaultBoundaryGroup?.id,
    });
  }, [defaultBoundaryGroup, isDefaultBoundaryGroupCustom]);

  const onSubmit = useCallback((results: BoundaryEditResults) => {
    if (!boundaryTerritoryGroup) {
      return;
    }

    dispatch(clearBoundaryLocationsFilter());
    dispatch(mapSettingsBoundariesClearIsolatedBoundaries());

    if (results.manualMethod === BoundaryEditManualMethod.Selection) {
      const selectedBoundaryGroup = getBoundaryGroupById(results.selectedBoundaryGroupId);
      if (!selectedBoundaryGroup) {
        return;
      }
      if (
        CONFIG.ENABLE_TERRITORY_QUICK_EDIT &&
        (results.action === BoundaryEditAction.EditBoundaries) &&
        (sortedBoundaryList.length <= CLASSIC_EDIT_BOUNDARY_COUNT_THRESHOLD)
      ) {
        dispatch(boundarySelectQuickEditCustomBoundaryOpenModal({
          boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
          preselectedBucketId: props.boundaryId,
          buckets: sortedBoundaryList.map(({ id, displayName }) => {
            const bucketStyle = getBoundaryStyle(boundaryTerritoryGroup, id);

            return {
              bucketId: id,
              name: displayName,
              colorInfo: hexColorToBucketColorInfo(bucketStyle.color),
            } satisfies BoundarySelectBucket;
          }),
          selectBoundaryGroupId: selectedBoundaryGroup.id,
          onClose: () => {
            dispatch(boundarySelectEditCloseModal());
          },
          onSubmit: (selectState) => {
            showLoader();

            dispatch(boundarySelectQuickEditSubmitted({
              selectState,
              baseBoundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
              onDone: () => {
                dispatch(boundarySelectEditCloseModal());
                hideLoader();
              },
            }));
          },
        }));
      }
      else {
        dispatch(boundarySelectEditCustomBoundaryOpenModal({
          actionType: results.action,
          boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
          selectBoundaryGroupId: selectedBoundaryGroup.id,
          boundaryId: props.boundaryId,
          onClose: () => {
            dispatch(boundarySelectEditCloseModal());
          },
          onSubmit: (selectState) => {
            onBoundarySelectEditCustomBoundarySubmit(selectState);
          },
        }));
      }
    }

    if (results.manualMethod === BoundaryEditManualMethod.Draw) {
      dispatch(boundaryDrawAdvancedEditOpenModal({
        boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
        boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
        action: results.action,
        boundaryId: props.boundaryId,
      }));
    }

    const onClose = props.onClose;
    onClose();
  }, [boundaryTerritoryGroup, dispatch, getBoundaryGroupById, hideLoader, onBoundarySelectEditCustomBoundarySubmit, props.boundaryId, props.onClose, showLoader, sortedBoundaryList]);

  if (!boundaryTerritoryGroup || !checkBoundaryIsCustom(editedBoundaryGroup)) {
    return null;
  }

  const boundaryGroupIsEmpty = !sortedBoundaryList?.length;

  return (
    <BoundaryEditModalComponent
      isOpen={props.isOpen}
      onClose={props.onClose}
      onSubmit={onSubmit}
      boundaryGroupIsEmpty={boundaryGroupIsEmpty}
      globalBoundaryGroups={globalBoundaryGroups}
      customBoundaryGroups={customBoundaryGroups}
      defaultAction={isAdvancedEdit ? BoundaryEditAction.EditBoundaries : undefined}
      defaultManualMethod={defaultManualMethod}
      defaultSelectFrom={defaultSelectFrom}
      defaultBoundaryGroup={defaultBoundaryGroupParams}
    />
  );
};
